import { EditNoteOutlined } from '@mui/icons-material'
import { Alert, Button, Stack, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { ConfigFileChangeReview } from '@/features/controller/configuration/components/ConfigFileChangeReview'
import { ConfigReviewDialog } from '@/features/controller/configuration/components/ConfigReviewDialog'
import type { ControllerConfigVersion, ProposedEditNavigationState } from '@/features/controller/configuration/types'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'

export type TreeConfigChange = {
  baseVersion: ControllerConfigVersion
  /** A save conflicted, so baseVersion was saved meanwhile and the change would overwrite it */
  baseReplaced: boolean
  value: string
}

type TreeReviewEditsDialogProps = {
  blockingErrors: string[]
  changes: TreeConfigChange[]
  comment: string
  controllerId: string
  isFetchingNewerVersion: boolean
  isUpdating: boolean
  onClose: () => void
  onCommentChange: (comment: string) => void
  onSave: () => void
  onSaveAndRestart: () => void
  open: boolean
  /** The last save stored one file but not the other */
  partialSaveWarning: string | null
}

export function TreeReviewEditsDialog({
  blockingErrors,
  changes,
  comment,
  controllerId,
  isFetchingNewerVersion,
  isUpdating,
  onClose,
  onCommentChange,
  onSave,
  onSaveAndRestart,
  open,
  partialSaveWarning,
}: Readonly<TreeReviewEditsDialogProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  function openInTextEditor({ baseVersion, baseReplaced, value }: TreeConfigChange) {
    navigate(getControllerConfigType(baseVersion.configurationTypeId).navigationPath(controllerId), {
      state: {
        proposedEdit: { previousVersionId: baseVersion.id, value, comment, baseReplaced },
      } satisfies ProposedEditNavigationState,
    })
  }

  return (
    <ConfigReviewDialog
      blockingErrors={blockingErrors}
      comment={comment}
      hasChanges={changes.length > 0}
      isFetchingNewerVersion={isFetchingNewerVersion}
      isUpdating={isUpdating}
      open={open}
      overwritesNewerVersion={changes.some((change) => change.baseReplaced)}
      onClose={onClose}
      onCommentChange={onCommentChange}
      onSave={onSave}
      onSaveAndRestart={onSaveAndRestart}
    >
      {partialSaveWarning && <Alert severity="warning">{partialSaveWarning}</Alert>}
      {/* A standing hint, not news: role="note" keeps it out of the alerts announced to screen readers */}
      <Alert role="note" severity="info">
        {t('controllers.configuration.review_dialog.tree_rewrites_files')}
      </Alert>
      {changes.map((change) => {
        const configType = getControllerConfigType(change.baseVersion.configurationTypeId)
        return (
          <ConfigFileChangeReview
            key={configType.id}
            baseReplaced={change.baseReplaced}
            header={
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <CustomTypography variant="h5">
                  {t('controllers.configuration.tree.unsaved_changes.title', { configTypeName: configType.name })}
                </CustomTypography>
                <Tooltip
                  describeChild={true}
                  title={
                    changes.length > 1
                      ? t('controllers.configuration.review_dialog.open_in_text_editor_drops_other_file')
                      : ''
                  }
                >
                  {/* CustomButton does not forward the ref the tooltip anchors to */}
                  <Button
                    startIcon={<EditNoteOutlined />}
                    type="button"
                    variant="text"
                    onClick={() => openInTextEditor(change)}
                  >
                    {t('controllers.configuration.review_dialog.open_in_text_editor')}
                  </Button>
                </Tooltip>
              </Stack>
            }
            language={configType.textEditorLanguage}
            modifiedText={change.value}
            originalText={change.baseVersion.value}
          />
        )
      })}
    </ConfigReviewDialog>
  )
}
